/* ==========================================================================
   Enchanted Chaos Creations — site interactions
   Sticky header · mobile drawer (focus trap) · reveal-on-scroll ·
   newsletter validation · product gallery · quantity steppers · quick add ·
   contact validation.

   Shopify-aware: global behaviours use event delegation and the reveal /
   newsletter / contact initialisers re-run on `shopify:section:load`.
   ========================================================================== */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---- 1. Condensed sticky header ------------------------------------- */
  function mastheadHeight() {
    var m = document.querySelector(".masthead");
    return m ? m.offsetHeight : 200;
  }
  var threshold = 80;
  function measure() {
    threshold = Math.max(80, mastheadHeight() * 0.6);
  }
  function onScroll() {
    document.documentElement.classList.toggle("is-compact", window.scrollY > threshold);
  }
  measure();
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", function () {
    measure();
    onScroll();
  });

  /* ---- 2. Mobile drawer (delegated + focus trap) ---------------------- */
  var lastFocus = null;
  function drawer() {
    return document.getElementById("mobile-drawer");
  }
  function openDrawer() {
    var d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.classList.add("is-open");
    document.body.classList.add("no-scroll");
    document.querySelectorAll("[data-drawer-open]").forEach(function (b) {
      b.setAttribute("aria-expanded", "true");
    });
    var first = d.querySelector("a, button");
    if (first) first.focus();
  }
  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.classList.remove("is-open");
    document.body.classList.remove("no-scroll");
    document.querySelectorAll("[data-drawer-open]").forEach(function (b) {
      b.setAttribute("aria-expanded", "false");
    });
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  document.addEventListener("click", function (e) {
    var open = e.target.closest("[data-drawer-open]");
    if (open) {
      e.preventDefault();
      openDrawer();
      return;
    }
    var close = e.target.closest("[data-drawer-close]");
    if (close) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    var d = drawer();
    if (d && d.classList.contains("is-open")) {
      if (e.target === d) closeDrawer();
      if (e.target.closest('a[href^="#"]')) closeDrawer();
    }
  });
  document.addEventListener("keydown", function (e) {
    var d = drawer();
    if (!d || !d.classList.contains("is-open")) return;
    if (e.key === "Escape") {
      closeDrawer();
      return;
    }
    if (e.key === "Tab") {
      var focusables = d.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---- 3. Reveal on scroll -------------------------------------------- */
  var io = null;
  function initReveal(root) {
    var els = (root || document).querySelectorAll(".reveal:not(.is-visible)");
    if (!("IntersectionObserver" in window) || reduced) {
      els.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
      );
    }
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---- 4. Cart count --------------------------------------------------- */
  function updateCartCount(n) {
    document.querySelectorAll(".cart-count").forEach(function (c) {
      c.hidden = n <= 0;
      c.textContent = String(n);
    });
    document.querySelectorAll(".cart-icon").forEach(function (a) {
      a.setAttribute("aria-label", "Cart, " + n + (n === 1 ? " item" : " items"));
    });
  }
  function refreshCartCount() {
    if (window.fetch) {
      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
        })
        .catch(function () {});
    }
  }

  /* ---- 5. Newsletter form (Shopify customer form) --------------------- */
  function initNewsletter(root) {
    (root || document).querySelectorAll(".newsletter__form").forEach(function (form) {
      if (form.dataset.eccInit) return;
      form.dataset.eccInit = "1";
      form.setAttribute("novalidate", "");
      var input = form.querySelector('input[type="email"]');
      var error = form.querySelector(".field-error");
      function showError(msg) {
        if (error) error.textContent = msg;
        if (input) {
          if (msg) input.setAttribute("aria-invalid", "true");
          else input.removeAttribute("aria-invalid");
        }
      }
      if (input) {
        input.addEventListener("blur", function () {
          if (!input.value) return;
          showError(validEmail.test(input.value.trim()) ? "" : "That email looks off — mind checking it?");
        });
        input.addEventListener("input", function () {
          if (error && error.textContent) showError("");
        });
      }
      form.addEventListener("submit", function (e) {
        if (!input) return;
        var value = input.value.trim();
        if (!value) {
          e.preventDefault();
          showError("We need an email to add you to the Circle.");
          input.focus();
          return;
        }
        if (!validEmail.test(value)) {
          e.preventDefault();
          showError("That email looks off — mind checking it?");
          input.focus();
          return;
        }
        showError("");
        var submit = form.querySelector('button[type="submit"]');
        if (submit) {
          submit.disabled = true;
          submit.textContent = "Adding…";
        }
      });
    });
  }

  /* ---- 6. Contact form ------------------------------------------------- */
  function initContact(root) {
    var contact = (root || document).querySelector("#contact-form");
    if (!contact || contact.dataset.eccInit) return;
    contact.dataset.eccInit = "1";
    contact.setAttribute("novalidate", "");
    var fields = contact.querySelectorAll("[data-validate]");

    function errorFor(field) {
      return document.getElementById(field.id + "-error");
    }
    function isValid(field) {
      var value = (field.value || "").trim();
      if (field.type === "checkbox") return field.checked;
      if (!value) return false;
      if (field.type === "email") return validEmail.test(value);
      return true;
    }
    function checkField(field, show) {
      var ok = isValid(field);
      if (show) {
        var target = errorFor(field);
        if (target) target.textContent = ok ? "" : field.getAttribute("data-error") || "Please check this field.";
        if (ok) field.removeAttribute("aria-invalid");
        else field.setAttribute("aria-invalid", "true");
      }
      return ok;
    }
    fields.forEach(function (field) {
      field.addEventListener("blur", function () {
        checkField(field, true);
      });
      field.addEventListener("input", function () {
        if (field.getAttribute("aria-invalid") === "true") checkField(field, true);
      });
      field.addEventListener("change", function () {
        if (field.getAttribute("aria-invalid") === "true") checkField(field, true);
      });
    });
    contact.addEventListener("submit", function (e) {
      var allOk = true;
      var firstBad = null;
      fields.forEach(function (field) {
        if (!checkField(field, true)) {
          allOk = false;
          if (!firstBad) firstBad = field;
        }
      });
      if (!allOk) {
        e.preventDefault();
        if (firstBad) firstBad.focus();
        return;
      }
      var submit = contact.querySelector('button[type="submit"]');
      if (submit) {
        submit.disabled = true;
        submit.textContent = "Sending…";
      }
    });
  }

  /* ---- 7. Quantity steppers (display only; form handles the value) ---- */
  document.addEventListener("click", function (e) {
    var minus = e.target.closest("[data-qty-minus]");
    var plus = e.target.closest("[data-qty-plus]");
    if (!minus && !plus) return;
    var wrap = (minus || plus).closest("[data-qty]");
    if (!wrap) return;
    var field = wrap.querySelector(".qty__input");
    if (!field) return;
    var max = parseInt(field.getAttribute("max"), 10) || 99;
    var min = parseInt(field.getAttribute("min"), 10);
    if (isNaN(min)) min = 1;
    var n = parseInt(field.value, 10) || min;
    n = minus ? n - 1 : n + 1;
    n = Math.max(min, Math.min(max, n));
    field.value = String(n);
    field.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---- 8. Quick add (real cart) --------------------------------------- */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-quick-add]");
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var id = btn.getAttribute("data-variant-id");
    if (!id || !window.fetch) return;
    var original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = "Adding…";
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: Number(id), quantity: 1 }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error("add failed");
        return fetch("/cart.js", { headers: { Accept: "application/json" } });
      })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        btn.innerHTML = "Added";
        window.setTimeout(function () {
          btn.disabled = false;
          btn.innerHTML = original;
        }, 1200);
      })
      .catch(function (err) {
        if (window.console) console.warn("Quick add failed", err);
        btn.disabled = false;
        btn.innerHTML = "Try again";
        window.setTimeout(function () {
          btn.disabled = false;
          btn.innerHTML = original;
        }, 1800);
      });
  });

  /* ---- 9. Product gallery (real media) -------------------------------- */
  document.addEventListener("click", function (e) {
    var thumb = e.target.closest("[data-gallery-thumb]");
    if (!thumb) return;
    e.preventDefault();
    var src = thumb.getAttribute("data-media-src");
    var alt = thumb.getAttribute("data-media-alt") || "";
    var main = document.getElementById("pdp-main-image");
    if (!main || !src) return;
    document.querySelectorAll("[data-gallery-thumb]").forEach(function (b) {
      b.setAttribute("aria-pressed", String(b === thumb));
    });
    main.setAttribute("src", src);
    main.setAttribute("alt", alt);
  });

  /* ---- 10. Cart page: submit on quantity change ----------------------- */
  var cartTimer = null;
  document.addEventListener("change", function (e) {
    if (!e.target.classList || !e.target.classList.contains("qty__input")) return;
    var form = e.target.closest("form.cart-form");
    if (!form) return;
    if (cartTimer) clearTimeout(cartTimer);
    cartTimer = window.setTimeout(function () {
      form.submit();
    }, 450);
  });

  /* ---- Boot + Theme Editor re-init ------------------------------------ */
  function boot() {
    measure();
    onScroll();
    initReveal(document);
    initNewsletter(document);
    initContact(document);
    refreshCartCount();
  }
  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);

  document.addEventListener("shopify:section:load", function (e) {
    initReveal(e.target);
    initNewsletter(e.target);
    initContact(e.target);
    measure();
    onScroll();
  });
})();
